import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Container, Paper, ThemeProvider, Typography } from '@mui/material';
import gaiaTheme from '../../../root-components/theme';
import { LoadingSpinner } from '../../../components/presentation-components/display-elements-components/loading-spinner';
import BusinessCentralService from '../shared-services/business-central/business-central.service';
import { IBusinessCentralSignInStep } from '../shared-services/business-central/business-central.model';
import { BusinessCentralAppSteps } from './business-central-app-steps';

// appStepsClientId: when set, the steps inside Business Central are listed under the message.
type Outcome = {
  severity: 'success' | 'info' | 'warning' | 'error', title: string, message: string,
  appStepsClientId?: string, appStepsLink?: string | null,
};

const RETURN_NOTE = 'You can close this window. If you started from the integrations page, go back to it: it updates by itself.';

function outcomeForSignIn(result: IBusinessCentralSignInStep): Outcome {
  switch (result.outcome) {
    case 'connected':
      return {
        severity: 'success',
        title: 'Business Central is connected',
        message: result.companyCount > 0
          ? `${result.companyCount} ${result.companyCount === 1 ? 'company is' : 'companies are'} visible. ${RETURN_NOTE}`
          : `The tenant is linked, but no companies are visible yet. In Business Central, give the Microsoft Entra Application record a permission set for each company to report, then use Test connection on the integrations page.`,
      };
    case 'business-central-step':
      if (result.clientId) {
        return {
          severity: 'info',
          title: 'Access granted: one more step',
          message: 'Access is granted in Microsoft and your tenant is linked. One step is left, inside Business Central: '
            + 'Business Central keeps its own list of what each application may read, and a Business Central administrator '
            + 'adds ours to it once, as below.',
          appStepsClientId: result.clientId,
          appStepsLink: result.entraApplicationsUrl,
        };
      }
      return {
        severity: 'info',
        title: 'Access granted: one more step',
        message: `${result.message || 'Business Central is not reachable yet.'} Then use Test connection on the integrations page.`,
      };
    case 'environment-not-found':
      return {
        severity: 'info',
        title: 'Access granted: one more step',
        message: `${result.message || 'Business Central is not reachable yet.'} Then use Test connection on the integrations page.`,
      };
    case 'consent-missing':
      return {
        severity: 'warning',
        title: 'Access has not been granted yet',
        message: result.message || 'A Microsoft administrator needs to grant access first. Use the link you were sent, or Connect with Microsoft on the integrations page.',
      };
    case 'declined':
      return { severity: 'warning', title: 'Nothing was connected', message: result.message || 'The request was not completed.' };
    default:
      return { severity: 'error', title: 'Something went wrong', message: result.message || 'Business Central could not be reached. Please try again.' };
  }
}

/**
 * The public page Microsoft sends an administrator back to while connecting Business Central, both
 * after granting access (admin consent) and after the sign-in that links their tenant. It is
 * public because the administrator is often not a user of the platform. Everything is decided on
 * the server from the signed link in `state` and the code Microsoft returns; this page only hands
 * them over and says what happened.
 */
export const MicrosoftConnectPage = () => {
  const [searchParams] = useSearchParams();
  const businessCentralService = useMemo(() => new BusinessCentralService(), []);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  // A code can be redeemed once, so this must run once even where React runs effects twice.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const state = searchParams.get('state');
    const code = searchParams.get('code');
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    const fail = (err: any, fallback: string) => setOutcome({
      severity: 'error',
      title: 'Something went wrong',
      message: err?.response?.data?.message || fallback,
    });

    if (!state) {
      setOutcome({
        severity: 'warning',
        title: 'Nothing to connect',
        message: 'This page opens from a Microsoft sign-in link. Use the link you were sent again, or start from the integrations page.',
      });
      return;
    }

    if (code) {
      businessCentralService.signInReturned({ state, code, error, errorDescription })
        .then(result => setOutcome(outcomeForSignIn(result)))
        .catch(err => fail(err, 'The sign-in could not be completed. Please use the link again.'));
      return;
    }

    businessCentralService.consentReturned({ state, tenant: searchParams.get('tenant'), error, errorDescription })
      .then(result => {
        if (result.outcome === 'sign-in' && result.signInUrl) {
          // On to the sign-in that links the tenant. replace: the back button should not repeat consent.
          window.location.replace(result.signInUrl);
          return;
        }
        setOutcome({ severity: 'warning', title: 'Nothing was connected', message: result.message || 'The request was not completed.' });
      })
      .catch(err => fail(err, 'The consent could not be recorded. Please use the link again.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeProvider theme={gaiaTheme}>
      <Container maxWidth='sm' sx={{ mt: 6 }}>
        <Paper variant='outlined' sx={{ p: 4 }}>
          {!outcome ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minHeight: '160px', justifyContent: 'center' }}>
              <LoadingSpinner />
              <Typography variant='body2' sx={{ color: 'text.secondary' }}>Connecting Business Central</Typography>
            </Box>
          ) : (
            <>
              <Typography variant='h5' sx={{ mb: 2 }}>{outcome.title}</Typography>
              <Alert severity={outcome.severity}>{outcome.message}</Alert>
              {outcome.appStepsClientId && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant='subtitle1' sx={{ color: 'text.primary', mb: 1 }}>Allow the application in Business Central</Typography>
                  <BusinessCentralAppSteps clientId={outcome.appStepsClientId} entraApplicationsUrl={outcome.appStepsLink} />
                  <Typography variant='body2' sx={{ color: 'text.secondary', mt: 2 }}>
                    When that is done, let the person who asked you know, or use Test connection on the integrations
                    page. You can close this window.
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </ThemeProvider>
  );
};
